import { theme, type ThemeConfig } from 'antd';

const { defaultAlgorithm, darkAlgorithm } = theme;

/**
 * Govard CRM — Ant Design v6 Theme System
 *
 * Design direction D «Warm Precision» (owner decision 2026-10-05):
 * Linear-grade structure (hairline 1px borders, flat surfaces, radii 4/6/8,
 * compact 28px controls, 42px table rows, tabular numbers) combined with a
 * chat-native warm stone palette and a precise amber accent.
 *
 * Source of truth: `design-directions.html`, section [data-dir="d"].
 * Dark is a designed palette, not an inversion; all text pairs pass WCAG AA.
 *
 * Applied via ConfigProvider in App.tsx (ThemeContext) and mirrored into the
 * static `--ant-*` CSS variable sheet by `theme/themeVars.ts`.
 *
 * Spec aliases (light / dark):
 *   bg        #f9f7f4  / #141211   (colorBgLayout)
 *   surface   #fffdfa  / #292524   (colorBgContainer)
 *   surface-2 #f5f1eb  / #383533   (fills / hover)
 *   rail      #f5f1eb  / #1c1917   (Layout.siderBg)
 *   border    #e7e5e4  / #3a3430   (hairline — colorBorderSecondary)
 *   strong    #d6d3d1  / #57534e   (controls — colorBorder)
 *   text      #292524  / #f5f5f4   text-2 #57534e / #a8a29e  text-3 #6d6963 / #a8a29e
 *   accent    #b45309  / #f59e0b   (CTA; AA with its solid text color)
 *   bright    #d97706  / #f59e0b   (focus rings, markers, active bars)
 *   ok #15803d / #4ade80 · warn #c2410c / #fb923c · danger #b91c1c / #f87171
 */
export const govardLightTheme: ThemeConfig = {
  // Distinct cssVar keys per theme: cssinjs caches the token sheet by key,
  // so a shared key would freeze the first theme's values on toggle.
  cssVar: { prefix: 'ant', key: 'govard-light' },
  algorithm: defaultAlgorithm,
  token: {
    // ─── Brand: Warm Amber (CTA = amber-700, AA with white text) ─────────
    colorPrimary: '#b45309',
    colorSuccess: '#15803d',
    colorWarning: '#c2410c',
    colorError: '#b91c1c',
    colorInfo: '#2563eb',

    // Primary state variants
    colorPrimaryBg: '#fffbeb',
    colorPrimaryBgHover: '#fef3c7',
    colorPrimaryBorder: '#fbbf24',
    colorPrimaryBorderHover: '#d97706',
    colorPrimaryHover: '#92400e',
    colorPrimaryActive: '#92400e',
    colorPrimaryText: '#b45309',
    colorPrimaryTextHover: '#92400e',
    colorPrimaryTextActive: '#92400e',

    // ─── Typography: Stone palette ────────────────────────────────────────
    colorTextBase: '#292524',
    colorText: '#292524',
    colorTextHeading: '#292524',
    // Spec text levels: #292524 / #57534e / #6d6963 (all AA on light surfaces).
    // Quaternary stays lighter — hints and scrollbars only, never body text.
    colorTextSecondary: '#57534e',
    colorTextTertiary: '#6d6963',
    colorTextQuaternary: '#a8a29e',
    // Placeholder must stay AA on the white input surface (4.6:1).
    colorTextPlaceholder: '#6d6963',
    colorTextDisabled: '#d6d3d1',
    // Spec D font stack: native system UI + mono stack, base 13px / 1.45.
    fontFamily:
      "system-ui, -apple-system, 'Segoe UI Variable Text', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontFamilyCode:
      "ui-monospace, 'Cascadia Mono', 'SF Mono', 'JetBrains Mono', Consolas, 'Liberation Mono', monospace",
    fontSize: 13,
    lineHeight: 1.45,

    // ─── Surfaces: warm stone ────────────────────────────────────────────
    colorBgBase: '#fffdfa',
    colorBgContainer: '#fffdfa',
    colorBgElevated: '#fffdfa',
    colorBgLayout: '#f9f7f4',
    colorBgSpotlight: '#292524',
    colorBgMask: 'rgba(41, 37, 36, 0.45)',

    // Semantic bg/border variants
    colorSuccessBg: '#f0fdf4',
    colorSuccessBgHover: '#dcfce7',
    colorSuccessBorder: '#86efac',
    colorSuccessBorderHover: '#4ade80',
    colorSuccessHover: '#15803d',
    colorSuccessActive: '#166534',
    colorSuccessText: '#15803d',
    colorSuccessTextHover: '#166534',
    colorSuccessTextActive: '#14532d',

    colorWarningBg: '#fff7ed',
    colorWarningBgHover: '#ffedd5',
    colorWarningBorder: '#fdba74',
    colorWarningBorderHover: '#fb923c',
    colorWarningHover: '#c2410c',
    colorWarningActive: '#9a3412',
    colorWarningText: '#c2410c',
    colorWarningTextHover: '#9a3412',
    colorWarningTextActive: '#7c2d12',

    colorErrorBg: '#fef2f2',
    colorErrorBgHover: '#fee2e2',
    colorErrorBorder: '#fca5a5',
    colorErrorBorderHover: '#f87171',
    colorErrorHover: '#b91c1c',
    colorErrorActive: '#991b1b',
    colorErrorText: '#b91c1c',
    colorErrorTextHover: '#991b1b',
    colorErrorTextActive: '#7f1d1d',

    colorInfoBg: '#eff6ff',
    colorInfoBgHover: '#dbeafe',
    colorInfoBorder: '#93c5fd',
    colorInfoBorderHover: '#60a5fa',
    colorInfoHover: '#1d4ed8',
    colorInfoActive: '#1e40af',
    colorInfoText: '#2563eb',
    colorInfoTextHover: '#1d4ed8',
    colorInfoTextActive: '#1e40af',

    // ─── Links follow the accent-text ramp ───────────────────────────────
    colorLink: '#b45309',
    colorLinkHover: '#92400e',
    colorLinkActive: '#92400e',

    // ─── Borders: hairline discipline ────────────────────────────────────
    // colorBorder = strong border for controls (inputs, buttons);
    // colorBorderSecondary = hairline for cards, panels, dividers.
    colorBorder: '#d6d3d1',
    colorBorderSecondary: '#e7e5e4',

    // ─── Geometry: spec radii 4 / 6 / 8 ──────────────────────────────────
    borderRadiusXS: 6,
    borderRadiusSM: 6,
    borderRadius: 10,
    borderRadiusLG: 14,

    // ─── Density: 28px controls, spec row height handled by Table tokens ──
    controlHeight: 28,
    controlHeightSM: 24,
    controlHeightLG: 32,

    // ─── Spacing ──────────────────────────────────────────────────────────
    padding: 16,
    paddingSM: 12,
    paddingLG: 24,
    margin: 16,
    marginSM: 12,
    marginLG: 24,

    // ─── Motion: Linear-grade 110ms ──────────────────────────────────────
    motionDurationFast: '0.11s',
    motionDurationMid: '0.11s',

    // ─── Shadows: flat surfaces; pop shadow only for floating layers ─────
    boxShadow: 'none',
    boxShadowSecondary: '0 1px 3px rgba(41, 37, 36, 0.08), 0 4px 12px rgba(41, 37, 36, 0.06)',
    boxShadowTertiary: '0 1px 3px rgba(41, 37, 36, 0.08)',
  },

  components: {
    Layout: {
      siderBg: '#f5f1eb',
      headerBg: '#fffdfa',
      bodyBg: '#f9f7f4',
    },
    Menu: {
      itemBg: 'transparent',
      itemSelectedBg: '#fffbeb',
      itemSelectedColor: '#b45309',
      itemHoverBg: '#f5f1eb',
    },
    Table: {
      headerBg: 'transparent',
      // Same secondary gray as colorTextSecondary: 7.1:1 on the card surface.
      headerColor: '#57534e',
      rowHoverBg: '#f5f1eb',
      borderColor: '#e7e5e4',
      // fontSize 13 × lineHeight 1.45 + 2×11 ≈ 42px rows (spec).
      cellPaddingBlock: 11,
      cellPaddingInline: 14,
      headerSplitColor: 'transparent',
    },
    Button: {
      primaryShadow: 'none',
      defaultShadow: 'none',
      dangerShadow: 'none',
      // Spec .btn weight 550; primary solid text white (AA on amber-700).
      fontWeight: 550,
      primaryColor: '#ffffff',
      defaultBg: '#fffdfa',
      defaultBorderColor: '#d6d3d1',
      defaultHoverBg: '#fffbeb',
      defaultColor: '#292524',
    },
    Input: {
      activeShadow: '0 0 0 2px rgba(217, 119, 6, 0.12)',
      colorBorder: '#d6d3d1',
      colorBgContainer: '#ffffff',
      hoverBorderColor: '#d97706',
      activeBorderColor: '#b45309',
    },
    InputNumber: {
      activeShadow: '0 0 0 2px rgba(217, 119, 6, 0.12)',
      colorBorder: '#d6d3d1',
      colorBgContainer: '#ffffff',
      hoverBorderColor: '#d97706',
      activeBorderColor: '#b45309',
    },
    Select: {
      colorBorder: '#d6d3d1',
      colorBgContainer: '#ffffff',
      hoverBorderColor: '#d97706',
      activeBorderColor: '#b45309',
    },
    DatePicker: {
      colorBorder: '#d6d3d1',
      colorBgContainer: '#ffffff',
      hoverBorderColor: '#d97706',
      activeBorderColor: '#b45309',
    },
    Card: {
      headerBg: 'transparent',
    },
    Checkbox: {
      borderRadiusSM: 6,
    },
    Drawer: {
      colorBgElevated: '#fffdfa',
      colorBgMask: 'rgba(41, 37, 36, 0.45)',
    },
    Modal: {
      headerBg: '#fffdfa',
      contentBg: '#fffdfa',
    },
    Dropdown: {
      colorBgElevated: '#fffdfa',
    },
    Tooltip: {
      colorBgSpotlight: '#fffbeb',
      colorTextLightSolid: '#92400e',
    },
    Popover: {
      colorBgElevated: '#fffdfa',
    },
    Segmented: {
      // Spec .seg: stone track with a soft-amber active pill.
      trackBg: '#f5f1eb',
      itemSelectedBg: '#fffbeb',
      itemSelectedColor: '#b45309',
    },
    Tabs: {
      inkBarColor: '#d97706',
      itemSelectedColor: '#b45309',
      itemHoverColor: '#92400e',
    },
    Statistic: {
      // Spec KPI value: 22px / 600 with tabular numbers.
      contentFontSize: 22,
    },
  },
};

export const govardDarkTheme: ThemeConfig = {
  cssVar: { prefix: 'ant', key: 'govard-dark' },
  algorithm: darkAlgorithm,
  token: {
    // ─── Brand: amber accent with dark solid text (AA 7.9:1) ─────────────
    // (colorInfo lives in the semantic section below: blue-400 in dark.)
    colorPrimary: '#f59e0b',
    colorSuccess: '#4ade80',
    colorWarning: '#fb923c',
    colorError: '#f87171',

    colorPrimaryBg: '#2e1e0a',
    colorPrimaryBgHover: '#452b0f',
    colorPrimaryBorder: '#78350f',
    colorPrimaryBorderHover: '#f59e0b',
    colorPrimaryHover: '#fbbf24',
    colorPrimaryActive: '#fbbf24',
    colorPrimaryText: '#f59e0b',
    colorPrimaryTextHover: '#fbbf24',
    colorPrimaryTextActive: '#fde68a',

    // ─── Typography: Dark Stone (spec: text-2 == text-3, AA everywhere) ──
    colorTextBase: '#f5f5f4',
    colorText: '#f5f5f4',
    colorTextHeading: '#f5f5f4',
    colorTextSecondary: '#a8a29e',
    colorTextTertiary: '#a8a29e',
    colorTextQuaternary: '#78716c',
    // Placeholder must stay AA on the dark input surface (6.8:1).
    colorTextPlaceholder: '#a8a29e',
    colorTextDisabled: '#57534e',
    fontFamily:
      "system-ui, -apple-system, 'Segoe UI Variable Text', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontFamilyCode:
      "ui-monospace, 'Cascadia Mono', 'SF Mono', 'JetBrains Mono', Consolas, 'Liberation Mono', monospace",
    fontSize: 13,
    lineHeight: 1.45,

    // ─── Surfaces: Dark Stone (#1c1917 base, #292524 container) ─────────
    colorBgBase: '#1c1917',
    colorBgContainer: '#292524',
    colorBgElevated: '#383533',
    colorBgLayout: '#141211',
    colorBgSpotlight: '#383533',
    colorBgMask: 'rgba(12, 10, 9, 0.65)',

    // Semantic bg/border variants
    colorSuccessBg: '#052e16',
    colorSuccessBgHover: '#14532d',
    colorSuccessBorder: '#166534',
    colorSuccessBorderHover: '#22c55e',
    colorSuccessHover: '#86efac',
    colorSuccessActive: '#bbf7d0',
    colorSuccessText: '#4ade80',
    colorSuccessTextHover: '#86efac',
    colorSuccessTextActive: '#bbf7d0',

    colorWarningBg: '#431407',
    colorWarningBgHover: '#7c2d12',
    colorWarningBorder: '#9a3412',
    colorWarningBorderHover: '#fb923c',
    colorWarningHover: '#fdba74',
    colorWarningActive: '#fde68a',
    colorWarningText: '#fb923c',
    colorWarningTextHover: '#fdba74',
    colorWarningTextActive: '#fde68a',

    colorErrorBg: '#450a0a',
    colorErrorBgHover: '#7f1d1d',
    colorErrorBorder: '#991b1b',
    colorErrorBorderHover: '#fca5a5',
    colorErrorHover: '#fca5a5',
    colorErrorActive: '#fecaca',
    colorErrorText: '#f87171',
    colorErrorTextHover: '#fca5a5',
    colorErrorTextActive: '#fecaca',

    colorInfoBg: '#172554',
    colorInfoBgHover: '#1e3a8a',
    colorInfoBorder: '#1e40af',
    colorInfoBorderHover: '#60a5fa',
    // Info base is blue-400 in dark: Tag status text reads colorInfo on
    // colorInfoBg, and #3b82f6 lands at 4.0:1 there — below AA for 12px tags.
    colorInfo: '#60a5fa',
    colorInfoHover: '#93c5fd',
    colorInfoActive: '#bfdbfe',
    colorInfoText: '#93c5fd',
    colorInfoTextHover: '#bfdbfe',
    colorInfoTextActive: '#dbeafe',

    // ─── Links follow the accent-text ramp ───────────────────────────────
    colorLink: '#f59e0b',
    colorLinkHover: '#fbbf24',
    colorLinkActive: '#fde68a',

    // ─── Borders: hairline discipline ────────────────────────────────────
    colorBorder: '#57534e',
    colorBorderSecondary: '#3a3430',

    // ─── Geometry: spec radii 4 / 6 / 8 ──────────────────────────────────
    borderRadiusXS: 6,
    borderRadiusSM: 6,
    borderRadius: 10,
    borderRadiusLG: 14,

    // ─── Density: 28px controls, spec row height handled by Table tokens ──
    controlHeight: 28,
    controlHeightSM: 24,
    controlHeightLG: 32,

    // ─── Spacing ──────────────────────────────────────────────────────────
    padding: 16,
    paddingSM: 12,
    paddingLG: 24,
    margin: 16,
    marginSM: 12,
    marginLG: 24,

    // ─── Motion: Linear-grade 110ms ──────────────────────────────────────
    motionDurationFast: '0.11s',
    motionDurationMid: '0.11s',

    // ─── Shadows: flat surfaces; deep pop shadow for floating layers ─────
    boxShadow: 'none',
    boxShadowSecondary: '0 4px 16px rgba(0, 0, 0, 0.5)',
    boxShadowTertiary: '0 2px 8px rgba(0, 0, 0, 0.35)',
  },

  components: {
    Layout: {
      siderBg: '#1c1917',
      headerBg: '#292524',
      bodyBg: '#141211',
    },
    Menu: {
      itemBg: 'transparent',
      itemSelectedBg: '#2e1e0a',
      itemSelectedColor: '#f59e0b',
      itemHoverBg: '#383533',
    },
    Table: {
      headerBg: 'transparent',
      headerColor: '#a8a29e',
      rowHoverBg: '#383533',
      borderColor: '#3a3430',
      // fontSize 13 × lineHeight 1.45 + 2×11 ≈ 42px rows (spec).
      cellPaddingBlock: 11,
      cellPaddingInline: 14,
      headerSplitColor: 'transparent',
    },
    Button: {
      primaryShadow: 'none',
      defaultShadow: 'none',
      dangerShadow: 'none',
      fontWeight: 550,
      // Amber-500 CTA carries dark text (spec --on-accent #241a05, AA 7.9:1).
      primaryColor: '#241a05',
      dangerColor: '#450a0a',
      defaultBg: '#292524',
      defaultBorderColor: '#57534e',
      defaultHoverBg: '#383533',
      defaultColor: '#f5f5f4',
    },
    Input: {
      activeShadow: '0 0 0 2px rgba(245, 158, 11, 0.18)',
      colorBorder: '#57534e',
      colorBgContainer: '#1c1917',
      hoverBorderColor: '#f59e0b',
      activeBorderColor: '#f59e0b',
    },
    InputNumber: {
      activeShadow: '0 0 0 2px rgba(245, 158, 11, 0.18)',
      colorBorder: '#57534e',
      colorBgContainer: '#1c1917',
      hoverBorderColor: '#f59e0b',
      activeBorderColor: '#f59e0b',
    },
    Select: {
      colorBorder: '#57534e',
      colorBgContainer: '#1c1917',
      hoverBorderColor: '#f59e0b',
      activeBorderColor: '#f59e0b',
    },
    DatePicker: {
      colorBorder: '#57534e',
      colorBgContainer: '#1c1917',
      hoverBorderColor: '#f59e0b',
      activeBorderColor: '#f59e0b',
    },
    Card: {
      headerBg: 'transparent',
    },
    Checkbox: {
      borderRadiusSM: 6,
    },
    Drawer: {
      colorBgElevated: '#292524',
      colorBgMask: 'rgba(12, 10, 9, 0.65)',
    },
    Modal: {
      headerBg: '#292524',
      contentBg: '#292524',
    },
    Dropdown: {
      colorBgElevated: '#383533',
    },
    Tooltip: {
      colorBgSpotlight: '#452b0f',
      colorTextLightSolid: '#fcd34d',
    },
    Popover: {
      colorBgElevated: '#383533',
    },
    Segmented: {
      trackBg: '#141211',
      itemSelectedBg: '#2e1e0a',
      itemSelectedColor: '#f59e0b',
    },
    Tabs: {
      inkBarColor: '#f59e0b',
      itemSelectedColor: '#f59e0b',
      itemHoverColor: '#fbbf24',
    },
    Statistic: {
      contentFontSize: 22,
    },
  },
};

/** Default export for backwards compatibility */
export const govardTheme = govardLightTheme;
